// Restoran Siparişleri Raporu kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.restaurantReport';

export const salesRestaurantReportGuide = (t: TFunction): GuideContent => ({
  layout: 'report',
  title: t(`${G}.title`, 'Restoran Siparişleri Raporu nasıl okunur?'),
  intro: t(`${G}.intro`, 'Açık masaları, ödemesi alınmış ve iptal edilmiş adisyonları, sepetten silinen ürünleri tek sayfada görürsünüz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    actions: t(`${G}.regions.actions`, 'Yenile / Filtreler / Excel'),
    filters: t(`${G}.regions.filters`, 'Şube, arama, ödeme, tarih'),
    totals: t(`${G}.regions.totals`, 'Özet kartları'),
    table: t(`${G}.regions.table`, 'Sekmeler ve liste'),
  },
  steps: [
    {
      region: 'filters',
      title: t(`${G}.s1.title`, '1. Tarihi ve ekranı seçin'),
      text: t(`${G}.s1.text`, 'Rapor açıldığında bugünün kayıtlarını gösterir. "Filtreler" düğmesiyle filtre alanını açın; "Şube" listesinden bir restoran ekranı, "Başlangıç" ve "Bitiş" tarihlerini seçip "Filtrele"ye basın.'),
      tips: [
        t(`${G}.s1.tip1`, 'Tarih aralığı yalnız kapanmış adisyonlara uygulanır. "Aktif Masalar" her zaman şu anda açık olan masaları gösterir.'),
        t(`${G}.s1.tip2`, '"Ara (Masa, Satış No...)" kutusu yazdıkça listeyi süzer; masa adı, satış no, kapatan garson ve nota göre arar.'),
        t(`${G}.s1.tip3`, '"Ödeme Yöntemi" (Nakit, Kredi Kartı, Parçalı) yalnız "Tamamlanan Siparişler" listesini ve "Toplam Ciro" kartını etkiler.'),
      ],
    },
    {
      region: 'totals',
      title: t(`${G}.s2.title`, '2. Özet kartlarını okuyun'),
      text: t(`${G}.s2.text`, 'Kartlar ekrandaki filtre ve aramaya göre hesaplanır.'),
      tips: [
        t(`${G}.s2.tip1`, 'Aktif Masalar: şu anda açık masa sayısı. Aktif Toplam: bu masaların güncel tutarlarının toplamı.'),
        t(`${G}.s2.tip2`, 'Tamamlanan: ödemesi alınarak kapanan adisyon sayısı. Toplam Ciro: bu adisyonların toplam tutarı.'),
        t(`${G}.s2.tip3`, 'İptaller: ödeme alınmadan kapanan adisyonların toplam tutarı.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s3.title`, '3. Aktif Masalar ve Tamamlanan Siparişler'),
      text: t(`${G}.s3.text`, '"Aktif Masalar" sekmesinde açılış saati, süre, ürün sayısı, ara toplam ve toplam görünür. "Tamamlanan Siparişler" sekmesinde kapanış saati, süre, toplam, ödeme yöntemi ve masayı kapatan kişi yer alır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Süre: aktif masada açılıştan şu ana, kapanmış adisyonda açılıştan kapanışa geçen süredir. 60 dakikayı geçen açık masaların süresi turuncu görünür.'),
        t(`${G}.s3.tip2`, 'Yalnız nakit, kredi kartı / POS veya parçalı ödemeyle kapanan adisyonlar "Tamamlanan" sayılır.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Adisyon İptalleri ve Ürün İptalleri'),
      text: t(`${G}.s4.text`, '"Adisyon İptalleri" ödeme alınmadan kapanan adisyonları listeler; "Durum" sütunu nasıl kapandığını gösterir (ör. Ödenmedi, İptal). "Ürün İptalleri" kapanmış adisyonlarda sepetten silinen ("Silindi") veya miktarı azaltılan ("Miktar Azaltıldı") ürünleri garson, masa, silme nedeni ve satış no ile gösterir.'),
      tips: [
        t(`${G}.s4.tip1`, 'Tüm ürünleri başka masaya aktarıldığı için kendiliğinden kapanan adisyonlar da 0 tutarla "Adisyon İptalleri" sekmesinde görünür.'),
        t(`${G}.s4.tip2`, 'Hâlâ açık olan bir masadaki silmeler, masa kapandıktan sonra "Ürün İptalleri"nde görünür.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Detay ve garson işlemleri'),
      text: t(`${G}.s5.text`, 'Satırdaki "Sipariş Detayları" düğmesi ürünleri ve Ara Toplam, İndirim, KDV, Toplam dökümünü açar. "Garson İşlemleri" düğmesi o adisyonda kimin ne zaman ne yaptığını sırayla gösterir: masa açma, ürün ekleme / silme, mutfağa yazdırma, masa taşıma, ürün aktarma, indirim, ödeme, adisyon iptali.'),
    },
    {
      region: 'actions',
      title: t(`${G}.s6.title`, '6. Yenileyin, Excel\'e aktarın'),
      text: t(`${G}.s6.text`, '"Yenile" verileri yeniden yükler. "Excel İndir" ekranda filtrelenmiş "Tamamlanan Siparişler"i satış no, masa, masa grubu, açılış, kapanış, süre (dk), ara toplam, indirim, KDV, toplam, ödeme ve kapatan sütunlarıyla indirir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Liste uzunsa alttaki sayfalama ile ilerleyin; sayfa başına 10, 25 veya 50 satır seçebilirsiniz ("Ürün İptalleri" sayfada 10 satır gösterir).'),
        t(`${G}.s6.tip2`, 'Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
